import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminCustomerState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
  selectedRowKeys: any[];
  selectedRows: any[];
}
const defaultColumnList = [
  {
    name: 'name',
    label_key: 'name',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 1,
  },
  {
    name: 'email',
    label_key: 'email_address',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'mobile',
    label_key: 'phone_number',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'dob',
    label_key: 'date_of_birth',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'customer_group',
    label_key: 'customer_groups',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('customer_group') }),
    asyncKey: 'customer_group',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 4,
  },
  {
    name: 'status',
    label_key: 'status',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'status_all',
    selectKey: 'status',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 5,
  },
  {
    name: 'created_at',
    label_key: 'created_at',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 6,
  },
];
const exportColumnList = defaultColumnList.map((list) => ({
  ...list,
  allow_list: true,
  show_list_checkbox: true,
}));
const initialState: AdminCustomerState = {
  excludedFilter: [],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
  selectedRowKeys: [],
  selectedRows: [],
};

const adminCustomerSlice = createSlice({
  name: 'adminCustomer',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = { ...state.filterData, ...action.payload };
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setCustomerTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
    setSelectedRowKeys(state, action: PayloadAction<{ keys: any[]; rows?: any[] }>) {
      state.selectedRowKeys = action.payload.keys;
      if (action.payload.rows !== undefined) {
        state.selectedRows = action.payload.rows;
      }
    },
    resetSelectedRowKeys(state) {
      state.selectedRowKeys = [];
      state.selectedRows = [];
    },
  },
});

export const {
  setFilterData,
  resetFilterData,
  setReloadTableData,
  setCustomerTableColumnList,
  setSelectedRowKeys,
  resetSelectedRowKeys,
} = adminCustomerSlice.actions;

export default adminCustomerSlice.reducer;
